class ProductGwp extends HTMLElement {
  constructor() {
    super();
    this.gwpEl = this.querySelector('[data-gwp]');
    this.gwpAtcButton = this.querySelector('[data-gwp-atc-button]');
    this.gwpAtcButtonText = this.gwpAtcButton?.querySelector('[data-gwp-atc-text]');
    this.gwpAtcButtonSpinner = this.gwpAtcButton?.querySelector('[data-gwp-atc-spinner]');
    this.error = this.querySelector('[data-gwp-error]');
    this.errorMessage = this.querySelector('[data-gwp-error-message]');
    this.errorNoStock = this.querySelector('[data-gwp-error-no-stock]');
    this.variantImage = this.querySelector('[data-gwp-variant-image]');
    this.dismissButton = this.querySelector('[data-gwp-dismiss]');

    // Add image caching properties
    this.variantImagesCache = new Map();
    this.imagePreloadQueue = [];

    this.gwpAtcButton?.addEventListener('click', () => this.handleAtc(this));
    this.dismissButton?.addEventListener('click', () => this.handleDismiss());

    document.addEventListener('rebuy:cart.change', (event) => {
      const cart = event.detail.cart.cart;
      if (this.error) this.error.classList.add('hidden');
      this.updateGwp(cart);
    });

    // Listen for clicks on dropdown options
    this.gwpEl.querySelectorAll('[data-dropdown-option]').forEach((option) => {
      option.addEventListener('click', () => this.handleVariantChange());
    });

    // Add initial hidden state to prevent flash
    const elementsToHide = [
      this.querySelector('[data-gwp-description-and-bar]'),
      this.querySelector('[data-gwp-text-empty]'),
      this.querySelector('[data-gwp-text-progress]'),
      this.querySelector('[data-gwp-text-qualifies]'),
      this.querySelector('[data-gwp-text-success]'),
      this.querySelector('[data-gwp-variant-selectors]'),
    ];

    elementsToHide.forEach((element) => {
      if (element) element.classList.add('hidden');
    });

    // Immediately check initial state
    this.updateGwp();

    // Start prefetching variant images
    this.prefetchVariantImages();
  }

  getGwpBarProgress(cartSubtotal, thresholdValueCents) {
    return Math.min((cartSubtotal / thresholdValueCents) * 100, 100);
  }

  updateGwpBarProgress(progress) {
    const gwpBar = this.gwpEl.querySelector('[data-gwp-line]');

    gwpBar.style.width = `${progress}%`;
    gwpBar.classList.toggle('after:bg-[rgb(var(--color-brand-02))]', progress > 0);
    gwpBar.classList.toggle('after:bg-[#b1b1b1]', progress === 0);
    gwpBar.classList.toggle('after:mr-[9px]', progress > 50);
    gwpBar.classList.toggle('after:mr-0', progress <= 50);
  }

  updateGwpElements(progress, cartSubtotal, thresholdValueCents, isGiftInCart) {
    const gwpDescriptionAndBar = this.gwpEl.querySelector('[data-gwp-description-and-bar]');
    const gwpTextEmpty = this.gwpEl.querySelector('[data-gwp-text-empty]');
    const gwpTextProgress = this.gwpEl.querySelector('[data-gwp-text-progress]');
    const gwpTextQualifies = this.gwpEl.querySelector('[data-gwp-text-qualifies]');
    const gwpTextSuccess = this.gwpEl.querySelector('[data-gwp-text-success]');
    const progressPrice = gwpTextProgress.querySelector('[data-gwp-text-price]');
    const variantSelectors = this.gwpEl.querySelector('[data-gwp-variant-selectors]');

    // Update progress price
    progressPrice.textContent = ((thresholdValueCents - cartSubtotal) / 100).toFixed(2);

    // Set display of text strings
    if (gwpTextEmpty) gwpTextEmpty.classList.toggle('hidden', progress > 0);
    if (gwpTextProgress) gwpTextProgress.classList.toggle('hidden', progress === 0 || progress === 100);
    if (gwpDescriptionAndBar) gwpDescriptionAndBar.classList.toggle('hidden', progress === 100 && isGiftInCart);
    if (gwpTextQualifies) gwpTextQualifies.classList.toggle('hidden', !(progress === 100 && !isGiftInCart));
    if (gwpTextSuccess) gwpTextSuccess.classList.toggle('hidden', !(progress === 100 && isGiftInCart));
    if (variantSelectors) variantSelectors.classList.toggle('hidden', isGiftInCart);
  }

  checkForBuyItemInCart(cart) {
    const buyItemProductIds = this.gwpEl.dataset.gwpBuyProducts || false;
    if (buyItemProductIds) {
      const buyItemProductIdsArr = JSON.parse(buyItemProductIds);

      // Count total qualifying items in cart, including quantities
      const qualifyingItemsCount = cart.items.reduce((total, item) => {
        if (buyItemProductIdsArr.includes(parseInt(item.product_id, 10))) {
          return total + item.quantity;
        }
        return total;
      }, 0);

      return qualifyingItemsCount;
    } else {
      return 0;
    }
  }

  checkForGiftInCart(cart, widgetId) {
    // Count total GWP items in cart, including quantities
    const gwpCount = cart.items.reduce((total, item) => {
      if (parseInt(item.properties['_widget_id'], 10) === parseInt(widgetId, 10)) {
        return total + item.quantity;
      }
      return total;
    }, 0);

    return gwpCount;
  }

  updateBuyxGetyElements(gwpCount, qualifyingItemsCount) {
    const gwpDescriptionAndBar = this.gwpEl.querySelector('[data-gwp-description-and-bar]');
    const gwpTextEmpty = this.gwpEl.querySelector('[data-gwp-text-empty]');
    const gwpTextSuccess = this.gwpEl.querySelector('[data-gwp-text-success]');
    const variantSelectors = this.gwpEl.querySelector('[data-gwp-variant-selectors]');

    // Determine states
    const isGiftInCart = gwpCount > 0;
    const isBuyItemInCart = qualifyingItemsCount > 0;
    const showSuccessState = gwpCount === qualifyingItemsCount && qualifyingItemsCount > 0;

    // Set display of text strings
    if (gwpDescriptionAndBar) gwpDescriptionAndBar.classList.toggle('hidden', showSuccessState);
    if (gwpTextEmpty) gwpTextEmpty.classList.toggle('hidden', showSuccessState);
    if (variantSelectors) variantSelectors.classList.toggle('hidden', showSuccessState);
    if (gwpTextSuccess) gwpTextSuccess.classList.toggle('hidden', !showSuccessState);
  }

  updateGwpAtc(type, progress, gwpCount, qualifyingItemsCount, isDismissed = false) {
    if (type === 'gwp') {
      if (this.gwpAtcButton) {
        this.gwpAtcButton.classList.toggle('hidden', !(progress === 100 && !gwpCount));
      }
    } else if (type === 'buyxgety') {
      const showSuccessState = gwpCount === qualifyingItemsCount && qualifyingItemsCount > 0;
      const canAddMore = isDismissed || !showSuccessState;

      if (this.gwpAtcButton) {
        this.gwpAtcButton.setAttribute('data-gwp-qualified', qualifyingItemsCount >= 1);
        this.gwpAtcButton.classList.toggle('hidden', !canAddMore);
      }
    }
  }

  handleAtc() {
    const gwpData = JSON.parse(this.gwpEl.dataset.gwp);
    const widgetId = JSON.parse(this.gwpEl.dataset.gwpWidgetId);
    const isBuyxGety = !!this.gwpEl.dataset.gwpBuyProducts;

    if (this.error) {
      this.error.classList.add('hidden');
      this.errorMessage.classList.remove('hidden');
      this.errorNoStock.classList.add('hidden');
    }
    if (this.gwpAtcButton) this.gwpAtcButton.disabled = true;
    if (this.gwpAtcButtonText) this.gwpAtcButtonText.classList.add('hidden');
    if (this.gwpAtcButtonSpinner) this.gwpAtcButtonSpinner.classList.remove('hidden');

    const selectedVariant = {};
    this.gwpEl.querySelectorAll('[data-dropdown-menu]').forEach((dropdown, index) => {
      const selected = dropdown.querySelector('[data-dropdown-option="selected"]');
      const selectedValue = selected.dataset.dropdownOptionValue;
      selectedVariant[`option${index + 1}`] = selectedValue;
    });
    const selectedVariantObject = gwpData.variants.find(
      (variant) =>
        variant.option1 === selectedVariant.option1 &&
        (selectedVariant.option2 ? variant.option2 === selectedVariant.option2 : true) &&
        (selectedVariant.option3 ? variant.option3 === selectedVariant.option3 : true)
    );

    if (isBuyxGety) {
      const isQualified = this.gwpAtcButton.getAttribute('data-gwp-qualified') === 'true';
      this.resetButton();

      if (!isQualified) {
        this.error.classList.remove('hidden');
        return;
      }
    }

    if (selectedVariantObject && selectedVariantObject.available) {
      window.Rebuy.Cart.addItem({
        id: selectedVariantObject.id,
        quantity: 1,
        properties: { _widget_id: widgetId },
      });
    } else {
      this.error.classList.remove('hidden');
      this.errorMessage.classList.add('hidden');
      this.errorNoStock.classList.remove('hidden');
    }
  }

  resetButton() {
    if (this.gwpAtcButton) this.gwpAtcButton.disabled = false;
    if (this.gwpAtcButtonText) this.gwpAtcButtonText.classList.remove('hidden');
    if (this.gwpAtcButtonSpinner) this.gwpAtcButtonSpinner.classList.add('hidden');
  }

  async updateGwp(cartObject, isDismissed = false) {
    const widgetId = parseInt(this.gwpEl.dataset.gwpWidgetId);

    // If a cart object wasn't passed through with the function call, get the cart via AJAX.
    let cart = cartObject;
    if (!cart) {
      const response = await fetch(`${routes.cart_url}.js`);
      cart = await response.json();
    }

    // If dismissed, treat as if there are no qualifying items
    const gwpCount = isDismissed ? 0 : this.checkForGiftInCart(cart, widgetId);
    const qualifyingItemsCount = isDismissed ? 0 : this.checkForBuyItemInCart(cart);

    if (this.gwpEl.dataset.gwpThresholdValue) {
      const thresholdValue = parseInt(this.gwpEl.dataset.gwpThresholdValue);
      const thresholdValueCents = thresholdValue * 100;
      const progress = isDismissed ? 0 : this.getGwpBarProgress(cart.total_price, thresholdValueCents);
      this.updateGwpBarProgress(progress);
      this.updateGwpElements(progress, cart.total_price, thresholdValueCents, gwpCount);
      this.updateGwpAtc('gwp', progress, gwpCount, undefined, isDismissed);
    } else if (this.gwpEl.dataset.gwpBuyProducts) {
      this.updateBuyxGetyElements(gwpCount, qualifyingItemsCount);
      this.updateGwpAtc('buyxgety', undefined, gwpCount, qualifyingItemsCount, isDismissed);
    }

    // Remove initial hidden state after first update
    this.gwpEl.classList.remove('opacity-0');
  }

  handleVariantChange() {
    const gwpData = JSON.parse(this.gwpEl.dataset.gwp);
    const selectedVariant = {};

    // Get current selections
    this.gwpEl.querySelectorAll('[data-dropdown-menu]').forEach((dropdown, index) => {
      const selected = dropdown.querySelector('[data-dropdown-option="selected"]');
      const selectedValue = selected.dataset.dropdownOptionValue;
      selectedVariant[`option${index + 1}`] = selectedValue;
    });

    // Update available options in dropdowns
    this.updateAvailableOptions(gwpData, selectedVariant);

    const selectedVariantObject = gwpData.variants.find(
      (variant) =>
        variant.option1 === selectedVariant.option1 &&
        (selectedVariant.option2 ? variant.option2 === selectedVariant.option2 : true) &&
        (selectedVariant.option3 ? variant.option3 === selectedVariant.option3 : true)
    );

    // Update variant image if it exists
    if (this.variantImage && selectedVariantObject) {
      // Check if we have a cached image
      const cachedImage = this.variantImagesCache.get(selectedVariantObject.id);

      if (cachedImage) {
        // Use cached image
        this.variantImage.src = cachedImage.src;
        this.variantImage.alt = selectedVariantObject.featured_image.alt || gwpData.title;
      } else {
        // Fallback to regular loading if cache miss
        const variantImage = gwpData.images[selectedVariantObject.featured_image.position - 1];
        if (variantImage) {
          const currentWidth = this.variantImage.width * 2; // Multiply by 2 since we're displaying at half size
          const newImageUrl = variantImage.replace(/(\.[^.]*)$/, `_${currentWidth}x$1`);
          this.variantImage.src = newImageUrl;
          this.variantImage.alt = selectedVariantObject.featured_image.alt || gwpData.title;
        }
      }
    }

    // Update strikethrough price if it exists
    const strikethroughPrice = this.gwpEl.querySelector('[data-gwp-strikethrough-price]');
    const promoPrice = this.gwpEl.querySelector('[data-gwp-promo-price]');
    if (strikethroughPrice && selectedVariantObject) {
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(selectedVariantObject.price / 100);
      strikethroughPrice.textContent = formattedPrice;
    }

    if (promoPrice && selectedVariantObject) {
      const promoPriceType = promoPrice.getAttribute('data-promo-type');
      const promoPricePercent = parseFloat(promoPrice.dataset.promoPercent);
      const promoPriceAmount = parseFloat(promoPrice.dataset.promoAmount);
      if (promoPriceType === 'percent') {
        const formattedPrice = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format((selectedVariantObject.price - (selectedVariantObject.price * promoPricePercent) / 100) / 100);
        promoPrice.textContent = formattedPrice;
      } else if (promoPriceType === 'amount') {
        const formattedPrice = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format((selectedVariantObject.price - promoPriceAmount) / 100);
        promoPrice.textContent = formattedPrice;
      }
    }

    // Update button state based on variant availability
    if (selectedVariantObject && selectedVariantObject.available) {
      if (this.error) this.error.classList.add('hidden');
      if (this.gwpAtcButton) this.gwpAtcButton.disabled = false;
    } else {
      if (this.error) {
        this.error.classList.remove('hidden');
        this.errorMessage.classList.add('hidden');
        this.errorNoStock.classList.remove('hidden');
      }
      if (this.gwpAtcButton) this.gwpAtcButton.disabled = true;
    }
  }

  updateAvailableOptions(gwpData, currentSelections) {
    // Filter variants to only include non-excluded variants
    const availableVariants = gwpData.variants.filter((variant) => !variant.exclude);

    const dropdowns = this.gwpEl.querySelectorAll('[data-dropdown-menu]');

    // Handle first dropdown (Option1)
    const firstDropdown = dropdowns[0];
    if (firstDropdown) {
      const firstOptions = firstDropdown.querySelectorAll('[data-dropdown-option]');
      firstOptions.forEach((option) => {
        const value = option.dataset.dropdownOptionValue;
        const isValid = availableVariants.some((variant) => variant.option1 === value);

        const li = option.closest('li');
        if (li) {
          li.classList.toggle('hidden', !isValid);
        }
      });
    }

    // Handle second dropdown (Option2)
    const secondDropdown = dropdowns[1];
    if (secondDropdown) {
      const firstSelection = currentSelections.option1;
      const thirdSelection = currentSelections.option3;
      const secondOptions = secondDropdown.querySelectorAll('[data-dropdown-option]');

      secondOptions.forEach((option) => {
        const value = option.dataset.dropdownOptionValue;
        // Check if this combination exists with both Option1 and Option3
        const isValid = availableVariants.some(
          (variant) =>
            variant.option1 === firstSelection &&
            variant.option2 === value &&
            (thirdSelection ? variant.option3 === thirdSelection : true)
        );

        const li = option.closest('li');
        if (li) {
          li.classList.toggle('hidden', !isValid);
        }

        // If current selection is invalid, find first valid option
        const selectedOption = secondDropdown.querySelector('[data-dropdown-option="selected"]');
        if (selectedOption && selectedOption.dataset.dropdownOptionValue === value && !isValid) {
          // Find first valid option
          const firstValidOption = Array.from(secondOptions).find((opt) => {
            const validCombo = availableVariants.some(
              (variant) =>
                variant.option1 === firstSelection &&
                variant.option2 === opt.dataset.dropdownOptionValue &&
                variant.option3 === thirdSelection
            );
            return validCombo;
          });

          if (firstValidOption) {
            // Update selection
            selectedOption.setAttribute('data-dropdown-option', '');
            firstValidOption.setAttribute('data-dropdown-option', 'selected');

            // Update button text
            const button = secondDropdown.closest('custom-dropdown-menu').querySelector('[data-dropdown-text]');
            if (button) {
              button.textContent = firstValidOption.dataset.dropdownOptionValue;
            }

            // Update currentSelections
            currentSelections.option2 = firstValidOption.dataset.dropdownOptionValue;
          }
        }
      });
    }

    // Handle third dropdown (Option3)
    const thirdDropdown = dropdowns[2];
    if (thirdDropdown) {
      const thirdOptions = thirdDropdown.querySelectorAll('[data-dropdown-option]');
      thirdOptions.forEach((option) => {
        const value = option.dataset.dropdownOptionValue;
        const isValid = availableVariants.some((variant) => variant.option3 === value);

        const li = option.closest('li');
        if (li) {
          li.classList.toggle('hidden', !isValid);
        }
      });
    }
  }

  handleDismiss() {
    // Reset any error states
    if (this.error) this.error.classList.add('hidden');

    // Reset the button state
    this.resetButton();

    // Force update the GWP widget to show initial state with isDismissed flag
    this.updateGwp(null, true);
  }

  prefetchVariantImages() {
    if (!this.gwpEl?.dataset.gwp) return;

    try {
      const gwpData = JSON.parse(this.gwpEl.dataset.gwp);

      // Queue all variant images for preloading
      gwpData.variants.forEach((variant) => {
        if (variant.featured_image) {
          const variantImage = gwpData.images[variant.featured_image.position - 1];
          if (variantImage) {
            const currentWidth = this.variantImage ? this.variantImage.width * 2 : 212; // Default to 212 if width not available
            const imageUrl = variantImage.replace(/(\.[^.]*)$/, `_${currentWidth}x$1`);
            this.queueImagePreload(imageUrl, variant.id);
          }
        }
      });

      // Start preloading process
      this.processImageQueue();
    } catch (e) {
      console.error('Error prefetching GWP variant images:', e);
    }
  }

  queueImagePreload(imageUrl, variantId) {
    this.imagePreloadQueue.push({ imageUrl, variantId });
  }

  processImageQueue() {
    if (!this.imagePreloadQueue.length) return;

    const { imageUrl, variantId } = this.imagePreloadQueue.shift();

    const img = new Image();
    img.onload = () => {
      this.variantImagesCache.set(variantId, img);
      // Process next image in queue
      this.processImageQueue();
    };
    img.onerror = () => {
      console.warn(`Failed to preload GWP variant image for variant ID ${variantId}: ${imageUrl}`);
      // Continue processing the queue even if this image failed
      this.processImageQueue();
    };
    img.src = imageUrl;
  }
}

if (customElements.get('product-gwp') === undefined) {
  customElements.define('product-gwp', ProductGwp);
}
